import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../contexts/AuthContext';
import { useShell } from '../../shell/ShellContext';
import { ShellRail, ShellTopBarActions, useShellUI } from '../../shell/AppShell';
import { orphanMarketplaceAPI, startupMarketplaceAPI, playersAPI } from '../../utils/api';
import { ChevronRightIcon } from '../../shell/icons';

/**
 * ListingDetail — shell-native detail + inline apply for BOTH marketplaces,
 * mounted at the EXISTING public paths /orphan-marketplace/:id and
 * /startup-marketplace/:id (Postmark emails link here — paths immutable).
 * Logic per .claude/SHELL-REDESIGN-BRIEFS.md (Marketplaces); legacy
 * OrphanDetail/StartupDetail are superseded by this surface.
 */

const asArray = (data, ...keys) => {
  if (Array.isArray(data)) return data;
  for (const key of keys) {
    if (Array.isArray(data?.[key])) return data[key];
  }
  return [];
};

const settingsOf = (l) => l?.leagueSettings || {};
const metaOf = (l) => {
  const s = settingsOf(l);
  const parts = [];
  if (s.numTeams) parts.push(`${s.numTeams} teams`);
  parts.push(s.isSuperFlex ? 'SF' : '1QB');
  if (Number(s.ppr) >= 1) parts.push('PPR');
  else if (Number(s.ppr) > 0) parts.push('Half PPR');
  if (Number(s.tePremium) > 0) parts.push('TEP');
  return parts.join(' · ');
};
const buyInLabel = (l) => {
  const n = Number(l?.buyIn);
  return n ? `$${Math.round(n)}` : 'Free';
};
const paidThrough = (l) => {
  const years = Array.isArray(l?.yearsPaid) ? l.yearsPaid.map(Number).filter((y) => !Number.isNaN(y)) : [];
  return years.length ? Math.max(...years) : null;
};

const Fact = ({ label, value }) => (
  <div className="ft-card" style={{ padding: 14, textAlign: 'center' }}>
    <div style={{ fontSize: 16, fontWeight: 700 }}>{value}</div>
    <div className="ft-k" style={{ marginTop: 4, fontSize: 10 }}>{label}</div>
  </div>
);

const ListingDetail = ({ type }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { setContextLeague } = useShell();
  const { toast } = useShellUI();

  const api = type === 'orphan' ? orphanMarketplaceAPI : startupMarketplaceAPI;
  const basePath = type === 'orphan' ? '/orphan-marketplace' : '/startup-marketplace';

  const [listing, setListing] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [roster, setRoster] = useState([]);
  const [myApplication, setMyApplication] = useState(null);
  const [applicationsCount, setApplicationsCount] = useState(null);
  const [message, setMessage] = useState('');
  const [consent, setConsent] = useState(true);
  const [submitState, setSubmitState] = useState('idle'); // idle | submitting | done
  const [withdrawArmed, setWithdrawArmed] = useState(false);

  useEffect(() => {
    let active = true;
    setListing(null);
    setLoadError(false);
    api
      .getById(id)
      .then((res) => { if (active) setListing(res.data?.orphan || res.data?.startup || res.data?.listing || res.data); })
      .catch(() => { if (active) setLoadError(true); });
    return () => { active = false; };
  }, [api, id]);

  const isLister = !!(
    user && listing?.lister?.sleeperUsername &&
    user.sleeperUsername &&
    listing.lister.sleeperUsername.toLowerCase() === user.sleeperUsername.toLowerCase()
  );

  // Push the listing's league into the shell chip (ephemeral, Simulate pattern).
  useEffect(() => {
    if (!listing?.leagueName) return undefined;
    const s = settingsOf(listing);
    setContextLeague({
      leagueId: listing.sleeperLeagueId,
      name: listing.leagueName,
      numTeams: s.numTeams || 0,
      isSuperFlex: !!s.isSuperFlex,
      ppr: s.ppr || 0,
      tePremium: s.tePremium || 0,
    });
    return () => setContextLeague(null);
  }, [listing, setContextLeague]);

  // Resolve roster player ids → names, best first (uses the shared player cache endpoint).
  useEffect(() => {
    const ids = listing?.rosterData?.players;
    if (!Array.isArray(ids) || ids.length === 0) { setRoster([]); return undefined; }
    let active = true;
    const sf = !!settingsOf(listing).isSuperFlex;
    playersAPI
      .getBatch(ids)
      .then((res) => {
        if (!active) return;
        const map = res.data?.players || {};
        const named = Object.values(map)
          .map((p) => ({
            name: p.name,
            pos: p.position,
            value: Number((sf ? p.ktc_value_sf : p.ktc_value_1qb) ?? p.ktc_value_sf ?? 0),
          }))
          .filter((p) => p.name)
          .sort((a, b) => b.value - a.value);
        setRoster(named);
      })
      .catch(() => {});
    return () => { active = false; };
  }, [listing]);

  // My application status / owner application count.
  useEffect(() => {
    if (!user) return undefined;
    let active = true;
    api
      .getMyApplications()
      .then((res) => {
        if (!active) return;
        const mine = asArray(res.data, 'applications').find((a) => {
          const lid = a.orphanTeamId || a.startupListingId || a.orphanTeam?.id || a.startupListing?.id;
          return lid === id;
        });
        if (mine) setMyApplication(mine);
      })
      .catch(() => {});
    return () => { active = false; };
  }, [api, id, user]);

  useEffect(() => {
    if (!isLister) return undefined;
    let active = true;
    api
      .getApplications(id)
      .then((res) => { if (active) setApplicationsCount(asArray(res.data, 'applications').length); })
      .catch(() => {});
    return () => { active = false; };
  }, [api, id, isLister]);

  const submitApplication = async () => {
    if (!user?.sleeperUsername) {
      toast('Link your Sleeper account first');
      return;
    }
    setSubmitState('submitting');
    try {
      await api.apply(id, {
        sleeperUsername: user.sleeperUsername,
        message: message.trim(),
        emailShareConsent: consent,
      });
      setSubmitState('done');
      setMyApplication({ status: 'pending', message });
      toast('Application sent — the commissioner gets your dynasty resume with it');
    } catch (err) {
      setSubmitState('idle');
      toast(err?.response?.data?.error || 'Application failed — try again');
    }
  };

  const cornerstones = useMemo(() => roster.slice(0, 8), [roster]);

  if (loadError) {
    return (
      <div className="ft-view">
        <span className="ft-k brand">Marketplaces</span>
        <h1 className="ft-hero">Listing not found.</h1>
        <button className="ft-btn sm ghost" style={{ marginTop: 16 }} onClick={() => navigate('/marketplaces')}>
          Back to the marketplace
        </button>
      </div>
    );
  }
  if (!listing) {
    return <div className="ft-view"><div className="ft-loading">Opening the listing…</div></div>;
  }

  const adopted = listing.status && listing.status !== 'active';
  const applied = !!myApplication;
  const statusChip = adopted
    ? <span className="ft-chip">{type === 'orphan' ? 'Adopted' : 'Filled'}</span>
    : applied
      ? <span className="ft-chip green">Applied — {myApplication.status || 'pending'}</span>
      : <span className="ft-chip outline">Open</span>;

  return (
    <div className="ft-view">
      <Helmet><title>{listing.leagueName || 'Listing'} — Marketplaces — Fantasy Taco</title></Helmet>

      {isLister && (
        <ShellTopBarActions>
          {withdrawArmed ? (
            <>
              <span style={{ fontSize: 12.5, color: 'var(--ft-ink-2)' }}>Takes the listing down for good</span>
              <button
                className="ft-btn sm"
                onClick={async () => {
                  try {
                    await api.withdraw(id);
                    toast('Listing withdrawn');
                    navigate('/marketplaces' + (type === 'startup' ? '?type=startup' : ''));
                  } catch {
                    toast('Withdraw failed — try again');
                    setWithdrawArmed(false);
                  }
                }}
              >
                Confirm withdraw
              </button>
              <button className="ft-btn sm ghost" onClick={() => setWithdrawArmed(false)}>Cancel</button>
            </>
          ) : (
            <>
              <button className="ft-btn sm ghost" onClick={() => setWithdrawArmed(true)}>Withdraw</button>
              <button className="ft-btn sm ghost" onClick={() => navigate(`${basePath}/${id}/edit`)}>Edit</button>
              <button className="ft-btn sm" onClick={() => navigate(`${basePath}/${id}/applications`)}>
                Review applications{applicationsCount != null ? ` (${applicationsCount})` : ''}
              </button>
            </>
          )}
        </ShellTopBarActions>
      )}

      <ShellRail>
        <div className="ft-railsec">
          <span className="ft-k">This listing</span>
          <div className="ft-card" style={{ marginTop: 10 }}>
            <div style={{ fontSize: 15, fontWeight: 600 }}>{listing.leagueName}</div>
            <div style={{ fontSize: 12.5, color: 'var(--ft-ink-3)', margin: '3px 0 10px' }}>
              {metaOf(listing)} · {buyInLabel(listing)}
            </div>
            <div className="ft-pillrow">
              {listing.escrowType ? <span className="ft-chip outline">{listing.escrowType}</span> : null}
              {paidThrough(listing) ? <span className="ft-chip outline">Paid through {paidThrough(listing)}</span> : null}
            </div>
          </div>
        </div>
        {listing.lister?.sleeperUsername && (
          <div className="ft-railsec">
            <span className="ft-k">Listed by</span>
            <button
              className="ft-railaction"
              style={{ marginTop: 10 }}
              onClick={() => navigate(`/manager-intel/${listing.lister.sleeperUsername}`)}
            >
              <span>Scout {listing.lister.sleeperUsername}</span>
              <ChevronRightIcon />
            </button>
          </div>
        )}
        {user && !isLister && (
          <div className="ft-railsec">
            <span className="ft-k">Your application</span>
            {applied ? (
              <>
                <div className="ft-progressrow"><span>Submitted</span><span className="ft-chip green">Done</span></div>
                <div className="ft-progressrow">
                  <span>Commissioner review</span>
                  <span className="ft-chip">{myApplication.status === 'accepted' ? 'Accepted' : myApplication.status === 'rejected' ? 'Declined' : 'Pending'}</span>
                </div>
              </>
            ) : (
              <p className="ft-sub" style={{ fontSize: 13.5, marginTop: 8 }}>
                Your dynasty resume goes with it — record, titles, trade history.
              </p>
            )}
          </div>
        )}
        {isLister && (
          <div className="ft-railsec">
            <span className="ft-k">Pipeline</span>
            <div className="ft-progressrow">
              <span>Applications</span>
              <span className={`ft-chip${applicationsCount ? ' brand' : ''}`}>{applicationsCount ?? '—'}</span>
            </div>
            <div className="ft-progressrow">
              <span>Status</span>
              <span className="ft-chip outline">{adopted ? 'Closed' : 'Live'}</span>
            </div>
          </div>
        )}
      </ShellRail>

      <button className="ft-btn sm ghost" style={{ marginBottom: 22 }} onClick={() => navigate('/marketplaces' + (type === 'startup' ? '?type=startup' : ''))}>
        Back to listings
      </button>

      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 6 }}>
        {listing.leagueAvatarUrl ? (
          <img
            src={listing.leagueAvatarUrl}
            alt=""
            style={{ width: 52, height: 52, borderRadius: 14, objectFit: 'cover', flex: 'none' }}
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        ) : null}
        <div style={{ minWidth: 0 }}>
          <h1 className="ft-hero" style={{ fontSize: 28, margin: 0 }}>{listing.leagueName || 'League'}</h1>
          <div className="ft-pillrow" style={{ marginTop: 8 }}>
            <span className="ft-chip">{metaOf(listing)}</span>
            {listing.teamName ? <span className="ft-chip outline">{listing.teamName}</span> : null}
            {statusChip}
            {isLister ? <span className="ft-chip brand">Your listing</span> : null}
          </div>
        </div>
      </div>

      {listing.description ? (
        <p className="ft-sub" style={{ maxWidth: 640, marginTop: 18 }}>{listing.description}</p>
      ) : null}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, margin: '24px 0', maxWidth: 720 }}>
        <Fact label="Buy-in" value={buyInLabel(listing)} />
        <Fact label="Payment" value={listing.escrowType || 'Ask the commish'} />
        <Fact label="Paid through" value={paidThrough(listing) || '—'} />
        <Fact label="Starters" value={listing.numStarters || settingsOf(listing).rosterPositions?.filter((p) => p !== 'BN').length || '—'} />
      </div>

      {cornerstones.length > 0 && (
        <div className="ft-section" style={{ marginTop: 28 }}>
          <span className="ft-k">Roster cornerstones</span>
          <div className="ft-pillrow" style={{ marginTop: 12 }}>
            {cornerstones.map((p) => (
              <span key={p.name} className="ft-chip outline" style={{ fontSize: 13, padding: '7px 14px' }}>
                {p.name}{p.pos ? <span style={{ color: 'var(--ft-ink-3)', marginLeft: 5 }}>{p.pos}</span> : null}
              </span>
            ))}
            {roster.length > cornerstones.length ? (
              <span className="ft-chip">{roster.length - cornerstones.length} more</span>
            ) : null}
          </div>
        </div>
      )}

      {!adopted && !isLister && (
        <div className="ft-section" style={{ marginTop: 36, maxWidth: 560 }}>
          <span className="ft-k">{applied ? 'Your application' : 'Apply for this team'}</span>
          {applied ? (
            <p className="ft-sub" style={{ marginTop: 12 }}>
              Application in — {myApplication.status === 'accepted'
                ? 'you were accepted. Watch your email for the join link.'
                : myApplication.status === 'rejected'
                  ? 'the commissioner went another way this time.'
                  : 'the commissioner is reviewing.'}
            </p>
          ) : !user ? (
            <div style={{ marginTop: 14 }}>
              <p className="ft-sub" style={{ fontSize: 14, marginBottom: 14 }}>
                Sign in and your dynasty resume rides along automatically.
              </p>
              <button className="ft-btn" onClick={() => navigate('/login')}>Sign in to apply</button>
            </div>
          ) : (
            <div style={{ marginTop: 14 }}>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
                placeholder="Make your case"
                style={{
                  width: '100%', background: 'var(--ft-surface)', border: 'none',
                  borderRadius: 'var(--ft-r-input)', padding: '14px 16px', fontSize: 15, outline: 'none', resize: 'vertical',
                }}
              />
              <label style={{ display: 'flex', alignItems: 'center', gap: 9, margin: '14px 0 18px', fontSize: 13.5, color: 'var(--ft-ink-2)', cursor: 'pointer' }}>
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
                Share my email with the commissioner so they can reach me
              </label>
              <button className="ft-btn" disabled={submitState !== 'idle' || !consent} onClick={submitApplication}>
                {submitState === 'submitting' ? 'Sending…' : 'Submit application'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ListingDetail;
